import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { config } from "../config";
import { loadGoogleMaps } from "./googleMapsLoader";
import { readLatLngFromMapLocationEvent } from "./mapLocationEvent";
import {
  registerMap3dElement,
  unregisterMap3dElement,
} from "./mapMode";

// React wrapper around the experimental Google Maps 3D web component. The
// legacy code instantiated `<gmp-map-3d>` once and re-attached it to each
// page that needed it (see static/js/earth.js + main.js); in React we let
// the component own the element and tear it down on unmount.

export interface EarthMapHandle {
  /** The mounted gmp-map-3d element, or null if not yet ready. */
  getElement: () => google.maps.maps3d.Map3DElement | null;
}

interface Props {
  /** Initial camera focus. Defaults to legacy Vienna when not overridden. */
  initialCenter?: { lat: number; lng: number; altitude?: number };
  initialRange?: number;
  initialTilt?: number;
  initialHeading?: number;
  /** Fires whenever the user clicks on the map; payload is the lat/lng. */
  onMapClick?: (latLng: { lat: number; lng: number }) => void;
  /** Called once the underlying `gmp-map-3d` element is in the DOM. */
  onMapReady?: (el: HTMLElement) => void;
  /** Called right before the map element is removed (unmount or hot reload). */
  onMapDispose?: () => void;
}

export const EarthMap = forwardRef<EarthMapHandle, Props>(function EarthMap(
  {
    initialCenter = { lat: 48.203, lng: 16.396, altitude: 2175.13 },
    initialRange = 5000,
    initialTilt = 20,
    initialHeading = 0,
    onMapClick,
    onMapReady,
    onMapDispose,
  },
  ref,
) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.maps3d.Map3DElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({ getElement: () => mapRef.current }), []);

  useEffect(() => {
    let cancelled = false;
    let clickListener: ((e: Event) => void) | null = null;

    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !hostRef.current) return;

        const el = document.createElement(
          "gmp-map-3d",
        ) as unknown as google.maps.maps3d.Map3DElement;
        el.style.width = "100%";
        el.style.height = "100%";
        el.setAttribute(
          "center",
          `${initialCenter.lat},${initialCenter.lng},${initialCenter.altitude ?? 0}`,
        );
        el.setAttribute("range", String(initialRange));
        el.setAttribute("tilt", String(initialTilt));
        el.setAttribute("heading", String(initialHeading));
        el.setAttribute("mode", config.mapMode);
        el.mode = config.mapMode;
        // Scroll wheel zooms without Ctrl (legacy Map3DElement default was smoother UX).
        el.setAttribute("gesture-handling", "greedy");

        clickListener = (e) => {
          const latLng = readLatLngFromMapLocationEvent(e);
          if (latLng) onMapClick?.(latLng);
        };
        el.addEventListener("gmp-click", clickListener);
        el.addEventListener("click", clickListener);

        hostRef.current.appendChild(el);
        mapRef.current = el;
        registerMap3dElement(el);
        onMapReady?.(el);
        void google;
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load Google Maps");
      });

    return () => {
      cancelled = true;
      onMapDispose?.();
      if (mapRef.current) {
        unregisterMap3dElement(mapRef.current);
      }
      if (mapRef.current && clickListener) {
        mapRef.current.removeEventListener("gmp-click", clickListener);
        mapRef.current.removeEventListener("click", clickListener);
      }
      if (mapRef.current?.parentNode) {
        mapRef.current.parentNode.removeChild(mapRef.current);
      }
      mapRef.current = null;
    };
    // We deliberately only initialise once; subsequent prop changes don't
    // re-create the map (matches the legacy single-instance semantics).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={hostRef}
      style={{ width: "100%", height: "100%", position: "relative" }}
    >
      {error && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            color: "#600",
            background: "#fee",
          }}
        >
          Karte konnte nicht geladen werden: {error}
        </div>
      )}
    </div>
  );
});
